import React, {createContext, useContext, useState} from 'react';
import {checkNullStr} from "../utils/ReactUtils";

/**
 * Context to manage global toast messages.
 * @type {React.Context<{ showToast: (message: string) => void, toastMsg: string }>}
 */
const ToastContext = createContext();

const TAG = 'ToastProvider';
/**
 * Provider component to manage toast messages.
 * @component
 * @param {object} props - React component props.
 * @param {React.ReactNode} props.children - Child components to be wrapped by the provider.
 *
 * @example
 * //src/index.js: Add toast ToastProvider in your src->index.js file
 * import {ToastProvider} from "./fusion-ui/provider/ToastProvider";
 * const container = document.getElementById("root");
 * const root = createRoot(container);
 * root.render(<BrowserRouter >
 *       <ToastProvider>
 *           <App/>
 *       </ToastProvider>
 *    </BrowserRouter>
 * );
 *
 * //Add Toast in your root-component
 * import {Toast} from "./fusion-ui";
 * return (<>
 *     <RootComponent>
 *        ...
 *   </RootComponent>
 *
 *   <Toast/>
 * </>);
 *
 *
 * //Use toast in your component
 * import {useToast} from "./fusion-ui/provider/ToastProvider";
 * import {Button, Flexbox} from "./fusion-ui";
 *
 * const {showToast, toastMsg, toastHideDur, toastVariant, changeToastVariant} = useToast();
 *
 * return (
 *   <Flexbox>
 *        {['black', 'danger', 'warning', 'info', 'success', 'primary', ''].map((item, index) => {
 *            return <Button
 *                className={'text-nowrap mx-2 px-2 py-1'} key={index}
 *                variant={''}
 *                style={{ backgroundColor: `var(--${item})` }}
 *                hoverStyle={{ backgroundColor: `var(--${item})` }}
 *                color={'white'}
 *                width={'auto'}
 *                text={`${item} toast`}
 *                onClick={() => toast(`This is ${item} toast!`, item)}
 *            />
 *        })}
 *   </Flexbox>
 * );
 * function toast(text, variant) {
 *      showToast(text);
 *      changeToastVariant(variant);
 * }
 */
const ToastProvider = ({children}) => {
    const TOAST_HIDE_DURATION = 3000;

    /**
     * State to manage the current toast message.
     * @type {[string, function]}
     */
    const [toastMsg, setToastMsg] = useState('');
    const [toastHideDur, setToastDur] = useState(TOAST_HIDE_DURATION);
    const [toastVariant, setToastVariant] = useState('black');// 'success' | 'info' | 'warning' | 'error'

    /**
     * Function to show a toast message globally.
     * @function
     * @param {string} message - The message to be displayed in the toast.
     */
    const showToast = (message) => {
        setToastMsg(message);
    };
    const setToastHideDur = (dur) => {
        if (checkNullStr(dur)) setToastDur(dur);
    };

    const changeToastVariant = (variant) => {
        setToastVariant(variant);
    };

    return (<ToastContext.Provider value={{showToast, toastMsg, toastHideDur, setToastHideDur, toastVariant, changeToastVariant}}>
        {children}
    </ToastContext.Provider>);
};
/**
 * Custom hook to access the toast context values.
 * @function
 * @returns {{ showToast: function, toastMsg: string }} Toast context values.
 *
 * @example
 * import React from 'react';
 * import { useToast } from './ToastProvider';
 *
 * const YourComponent = () => {
 *   const { showToast, changeToastVariant } = useToast();
 *
 *   const handleClick = () => {
 *     toast('This is a toast message from another component!', 'primary');
 *   };
 *
 *   return (
 *     <div>
 *       <button onClick={handleClick}>Set Toast</button>
 *     </div>
 *   );
 *
 *   function toast(text, variant) {
 *         showToast(text +'');
 *         changeToastVariant(variant);
 *    }
 * };
 *
 * export default YourComponent;
 */
const useToast = () => {
    return useContext(ToastContext);
};
export {ToastProvider, useToast};
